import React, { ReactNode } from 'react';
import SalvPage from '../../../common/SalvPage';
import {
  useCreateScreeningSelectorGroup,
  useDeleteScreeningSelectorGroup,
  useQueryScreeningSelectors,
  useQueryScreeningSelectorsGroups,
  useUpdateScreeningSelectorGroup,
} from './useQueryScreeningSelectors';
import useCustomLists from '../../../custom-lists/useCustomLists';
import { DowJonesCustomListCode } from '../ScreeningDataSetup';
import {
  SalvProgressCircle,
  SalvTab,
  SalvTabList,
  SalvTabPanel,
  SalvTabs,
  SalvTooltipWrapper,
} from '@salvapp/ui-components';
import Selector from './Selector';
import './ListGroups.scss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleInfo } from '@fortawesome/pro-solid-svg-icons';
import ExternalLink from '../../../common/ExternalLink';
import { getConfigurationUrl } from '../../../navigation/helper';
import { ConfigurationProduct } from '../../../settings/configuration/Configuration';

export enum ManualCustomListSelectorCode {
  DJ_SANCTION_LISTS = 'DJ_SANCTION_LISTS',
  DJ_DESCRIPTIONS = 'DJ_DESCRIPTIONS',
}

// We construct our tree only from the PERSON option (ignoring ENTITY). When we choose lists we will account for both PERSON and ENTITY
export type SelectorRootOptionValue = 'DEFAULT_ROOT' | 'PERSON';

export const selectorConfigByType: {
  [key in ManualCustomListSelectorCode]: {
    fullName: string;
    name: string;
    description: ReactNode;
    selectorOptionHeaders: string[];
    rootOptionValue: SelectorRootOptionValue;
  };
} = {
  [ManualCustomListSelectorCode.DJ_SANCTION_LISTS]: {
    fullName: 'Dow Jones Programs',
    name: 'Programs',
    description: (
      <div>
        Profiles from Sanctions Lists, Other Official Lists and Other Exclusion Lists are sourced directly from official
        government and agency sources, with additional details from reliable public sources when appropriate and
        available. Learn more about content types{' '}
        <ExternalLink
          title={'here'}
          link={'https://help.salv.com/en/articles/234060-dow-jones-sanctions-lists-guide'}
        />
        .
      </div>
    ),
    selectorOptionHeaders: ['Authority', 'Provider', 'Type', 'Program'],
    rootOptionValue: 'PERSON',
  },
  [ManualCustomListSelectorCode.DJ_DESCRIPTIONS]: {
    fullName: 'Dow Jones Descriptions',
    name: 'Descriptions',
    description: (
      <div>
        Profiles compiled by Dow Jones researchers, sourced from licensed sources on Factiva. The lists cover, but are
        not limited to, individuals or entities reported, arrested, or convicted for serious crimes; companies owned or
        controlled by sanctioned individuals or entities; and companies linked to governments under comprehensive
        sanctions.
      </div>
    ),
    selectorOptionHeaders: ['Description 1', 'Description 2', 'Description 3'],
    rootOptionValue: 'DEFAULT_ROOT',
  },
};

const ListGroups = () => {
  const customLists = useCustomLists();
  const dowJonesListId = customLists.find((list) => list.code === DowJonesCustomListCode.DOW_JONES_SANCTION)?.id;
  const { data: unknownScreeningSelectors, isSuccess: isScreeningSelectorsLoaded } =
    useQueryScreeningSelectors(dowJonesListId);
  const screeningSelectors = unknownScreeningSelectors?.filter(
    (selector) => selectorConfigByType[selector.code as ManualCustomListSelectorCode]
  );
  const { data: screeningSelectorsGroups, isSuccess: isScreeningSelectorGroupsLoaded } =
    useQueryScreeningSelectorsGroups(dowJonesListId);

  const { mutateAsync: createGroup } = useCreateScreeningSelectorGroup(dowJonesListId);
  const { mutateAsync: updateGroup } = useUpdateScreeningSelectorGroup(dowJonesListId);
  const { mutate: deleteGroup } = useDeleteScreeningSelectorGroup(dowJonesListId);

  return (
    <SalvPage
      title={'List Groups'}
      breadcrumb={{
        title: 'Configuration',
        to: getConfigurationUrl(ConfigurationProduct.SCREENING),
      }}
      className={'list-groups'}
      subTitle={'Define Sanction List Groups to be used in Screening Flows.'}
    >
      {!isScreeningSelectorsLoaded || !isScreeningSelectorGroupsLoaded ? (
        <SalvProgressCircle />
      ) : (
        <SalvTabs>
          <SalvTabList>
            {screeningSelectors?.map((selector) => {
              const selectorConfig = selectorConfigByType[selector.code as ManualCustomListSelectorCode];
              return (
                <SalvTab
                  key={selector.code}
                  value={selector.code}
                  title={
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <div>{selectorConfig.fullName}</div>
                      <SalvTooltipWrapper tooltip={selectorConfig.description}>
                        <FontAwesomeIcon icon={faCircleInfo} className='text-muted' />
                      </SalvTooltipWrapper>
                    </div>
                  }
                />
              );
            })}
          </SalvTabList>
          {screeningSelectors?.map((selector) => (
            <SalvTabPanel key={selector.code} value={selector.code}>
              <Selector
                type={selector.code as ManualCustomListSelectorCode}
                apiSelectorOptions={selector.options}
                apiScreeningSelectorGroups={screeningSelectorsGroups.filter(
                  (group) => group.selectorCode === selector.code
                )}
                createGroup={createGroup}
                updateGroup={updateGroup}
                deleteGroup={deleteGroup}
              />
            </SalvTabPanel>
          ))}
        </SalvTabs>
      )}
    </SalvPage>
  );
};

export default ListGroups;
